<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CreateCustomerNotificationDialog from './CreateCustomerNotificationDialog.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const accountId = computed(() => route.params.accountId);

const records = computed(() => store.getters['customerNotifications/getCustomerNotifications']);
const meta = computed(() => store.getters['customerNotifications/getCustomerNotificationMeta']);
const filters = computed(() => store.getters['customerNotifications/getCustomerNotificationFilters']);
const uiFlags = computed(() => store.getters['customerNotifications/getCustomerNotificationUIFlags']);
const detail = computed(() => store.getters['customerNotifications/getCustomerNotificationDetail']);

const detailDialogRef = ref(null);
const createDialogRef = ref(null);
const resultDialogRef = ref(null);
const selectedId = ref(null);

const sendResult = computed(() => store.getters['customerNotifications/getCustomerNotificationSendResult']);

const onSent = () => {
  // Result is in store; open result dialog showing per-recipient breakdown
  resultDialogRef.value?.open();
};

const closeResult = () => {
  resultDialogRef.value?.close();
  store.dispatch('customerNotifications/clearSendResult');
};

const openNotificationFromResult = notificationId => {
  if (!notificationId) return;
  closeResult();
  // open detail of the freshly-created notification
  store.dispatch('customerNotifications/fetchDetail', notificationId).then(() => {
    selectedId.value = notificationId;
    detailDialogRef.value?.open();
  });
};

const recipientResultStatus = result => {
  if (result.error) return { key: result.error, tone: 'failed' };
  const d = result.delivery || {};
  if (d.delivered > 0) return { key: 'delivered', tone: 'delivered' };
  if (d.no_permission > 0) return { key: 'no_permission', tone: 'warn' };
  if (d.no_device_registered > 0) return { key: 'no_device_registered', tone: 'neutral' };
  if (d.failed > 0) return { key: 'failed', tone: 'failed' };
  return { key: 'pending', tone: 'neutral' };
};

const TONE_CLASS = {
  delivered: 'text-n-teal-11',
  warn: 'text-n-amber-11',
  neutral: 'text-n-slate-11',
  failed: 'text-n-ruby-11',
  optedOut: 'text-n-magenta-11',
};

const recipientTone = recipient => {
  const d = recipient.delivery || {};
  if ((d.delivered || 0) > 0) return 'delivered';
  if ((d.failed || 0) > 0) return 'failed';
  if ((d.opted_out || 0) > 0) return 'optedOut';
  if ((d.no_permission || 0) > 0) return 'warn';
  if ((d.no_device_registered || 0) > 0) return 'neutral';
  return 'neutral';
};

const recipientPillClass = r => {
  const tone = recipientTone(r);
  return `${TONE_CLASS[tone]} ${
    tone === 'delivered' ? 'bg-n-teal-3' :
    tone === 'failed' ? 'bg-n-ruby-3' :
    tone === 'warn' ? 'bg-n-amber-3' :
    tone === 'optedOut' ? 'bg-n-magenta-3' : 'bg-n-alpha-2'
  }`;
};

const recipientPillIcon = r => {
  const tone = recipientTone(r);
  if (tone === 'delivered') return 'i-lucide-check-check';
  if (tone === 'failed') return 'i-lucide-circle-x';
  if (tone === 'warn') return 'i-lucide-bell-off';
  if (tone === 'optedOut') return 'i-lucide-toggle-left';
  return 'i-lucide-info';
};

const recipientPillLabel = r => {
  const tone = recipientTone(r);
  const key = tone === 'delivered' ? 'DELIVERED' :
              tone === 'failed' ? 'FAILED' :
              tone === 'warn' ? 'NO_PERMISSION' :
              tone === 'optedOut' ? 'OPTED_OUT' : 'NO_DEVICE_REGISTERED';
  return t(`CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.${key}`);
};

// Chatwoot doesn't have a per-agent profile page, but the agents list
// settings page lets you find the user. Best-effort link.
const agentListUrl = () => `/app/accounts/${accountId.value}/settings/agents/list`;

const openAgent = (sender, event) => {
  if (!sender || sender.type !== 'user' || !sender.id) return;
  event?.stopPropagation();
  router.push(agentListUrl());
};

const isAbsoluteUrl = link => /^(https?:|mailto:|tel:)/i.test(link || '');

const CATEGORIES = [
  { value: '', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.ANY_CATEGORY' },
  { value: 'transactional', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.TRANSACTIONAL' },
  { value: 'marketing', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.MARKETING' },
  { value: 'system_alert', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.SYSTEM_ALERT' },
  { value: 'operator_broadcast', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.OPERATOR_BROADCAST' },
];

const READ_OPTIONS = [
  { value: 'any', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.READ_ANY' },
  { value: 'read', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.READ_YES' },
  { value: 'unread', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.READ_NO' },
];

const CONVERTED_OPTIONS = [
  { value: 'any', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.CONVERTED_ANY' },
  { value: 'yes', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.CONVERTED_YES' },
  { value: 'no', labelKey: 'CUSTOMER_NOTIFICATIONS.FILTER.CONVERTED_NO' },
];

const CATEGORY_STYLE = {
  transactional: 'bg-n-teal-3 text-n-teal-11',
  marketing: 'bg-n-magenta-3 text-n-magenta-11',
  system_alert: 'bg-n-amber-3 text-n-amber-11',
  operator_broadcast: 'bg-n-blue-3 text-n-blue-11',
};

const CATEGORY_ICON = {
  transactional: 'i-lucide-credit-card',
  marketing: 'i-lucide-tag',
  system_alert: 'i-lucide-triangle-alert',
  operator_broadcast: 'i-lucide-megaphone',
};

const STATUS_STYLE = {
  delivered: 'text-n-teal-11',
  no_permission: 'text-n-amber-11',
  no_device_registered: 'text-n-slate-11',
  failed: 'text-n-ruby-11',
};

const onFilterChange = (key, value) => {
  store.dispatch('customerNotifications/setFilter', { [key]: value || null });
  store.dispatch('customerNotifications/fetchList');
};

const onSearchInput = value => {
  store.dispatch('customerNotifications/setFilter', { search: value });
};

const onSearchSubmit = () => {
  store.dispatch('customerNotifications/fetchList');
};

const resetFilters = () => {
  store.dispatch('customerNotifications/resetFilters');
  store.dispatch('customerNotifications/fetchList');
};

const refresh = () => store.dispatch('customerNotifications/fetchList');

const loadMore = () => store.dispatch('customerNotifications/fetchMore');

const openDetail = async record => {
  selectedId.value = record.id;
  await store.dispatch('customerNotifications/fetchDetail', record.id);
  detailDialogRef.value?.open();
};

const onDetailClose = () => {
  selectedId.value = null;
  store.dispatch('customerNotifications/clearDetail');
};

const formatRelativeOrDate = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t('CUSTOMER_NOTIFICATIONS.TIME.JUST_NOW');
  if (diffMin < 60) return t('CUSTOMER_NOTIFICATIONS.TIME.MIN_AGO', { n: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t('CUSTOMER_NOTIFICATIONS.TIME.HR_AGO', { n: diffHours });
  return date.toLocaleString();
};

const formatDateTime = iso => (iso ? new Date(iso).toLocaleString() : '—');

const categoryLabel = value => {
  const found = CATEGORIES.find(c => c.value === value);
  return found ? t(found.labelKey) : value;
};

const contactProfileUrl = contactId =>
  `/app/accounts/${accountId.value}/contacts/${contactId}`;

const openContact = (contact, event) => {
  if (!contact?.id) return;
  // Don't bubble — card click would also fire openDetail otherwise
  event?.stopPropagation();
  router.push(contactProfileUrl(contact.id));
};

const openContactById = (contactId, event) => {
  if (!contactId) return;
  event?.stopPropagation();
  router.push(contactProfileUrl(contactId));
};

const fallbackRecipientLabel = record => {
  if (record.anon_device_id) return `anon · ${record.anon_device_id.slice(0, 8)}`;
  if (record.vivacrm_user_id) return `user · ${record.vivacrm_user_id.slice(0, 8)}`;
  return '—';
};

const deliveredTotal = record => {
  const d = record.delivery || {};
  return `${d.delivered || 0} / ${d.total_devices || 0}`;
};

const filtersActive = computed(() =>
  Boolean(
    filters.value.from ||
      filters.value.to ||
      filters.value.category ||
      filters.value.search ||
      (filters.value.read && filters.value.read !== 'any') ||
      (filters.value.converted && filters.value.converted !== 'any')
  )
);

onMounted(() => {
  store.dispatch('customerNotifications/fetchList');
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex flex-col">
            <span class="text-heading-1 text-n-slate-12">
              {{ t('CUSTOMER_NOTIFICATIONS.TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DESCRIPTION') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-rotate-cw"
              :label="t('CUSTOMER_NOTIFICATIONS.ACTIONS.REFRESH')"
              :disabled="uiFlags.fetchingList"
              @click="refresh"
            />
            <Button
              color="blue"
              size="sm"
              icon="i-lucide-plus"
              :label="t('CUSTOMER_NOTIFICATIONS.ACTIONS.CREATE')"
              @click="createDialogRef?.open()"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-2 flex flex-col gap-4">
        <!-- Filters -->
        <div class="flex flex-wrap items-end gap-2.5">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.FROM') }}
            </label>
            <input
              type="date"
              class="h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:outline-2"
              :value="filters.from || ''"
              @change="onFilterChange('from', $event.target.value)"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.TO') }}
            </label>
            <input
              type="date"
              class="h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:outline-2"
              :value="filters.to || ''"
              @change="onFilterChange('to', $event.target.value)"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.CATEGORY') }}
            </label>
            <select
              class="h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:outline-2"
              :value="filters.category || ''"
              @change="onFilterChange('category', $event.target.value)"
            >
              <option v-for="cat in CATEGORIES" :key="cat.value || 'any'" :value="cat.value">
                {{ t(cat.labelKey) }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.READ') }}
            </label>
            <select
              class="h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:outline-2"
              :value="filters.read"
              @change="onFilterChange('read', $event.target.value)"
            >
              <option v-for="opt in READ_OPTIONS" :key="opt.value" :value="opt.value">
                {{ t(opt.labelKey) }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.CONVERTED') }}
            </label>
            <select
              class="h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:outline-2"
              :value="filters.converted"
              @change="onFilterChange('converted', $event.target.value)"
            >
              <option v-for="opt in CONVERTED_OPTIONS" :key="opt.value" :value="opt.value">
                {{ t(opt.labelKey) }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5 flex-1 min-w-[200px]">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.FILTER.SEARCH') }}
            </label>
            <Input
              :model-value="filters.search"
              size="sm"
              :placeholder="t('CUSTOMER_NOTIFICATIONS.FILTER.SEARCH_PLACEHOLDER')"
              @update:model-value="onSearchInput"
              @enter="onSearchSubmit"
            />
          </div>
          <Button
            v-if="filtersActive"
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            :label="t('CUSTOMER_NOTIFICATIONS.FILTER.RESET')"
            @click="resetFilters"
          />
        </div>

        <!-- Meta row -->
        <div v-if="meta.totalUnread !== undefined" class="text-xs text-n-slate-11 px-1">
          {{ t('CUSTOMER_NOTIFICATIONS.META.UNREAD', { n: meta.totalUnread }) }}
        </div>

        <!-- List -->
        <div v-if="uiFlags.fetchingList && records.length === 0" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>
        <div v-else-if="records.length === 0" class="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div class="flex items-center justify-center w-16 h-16 rounded-2xl bg-n-alpha-2">
            <Icon icon="i-lucide-bell-off" class="text-n-slate-11 size-7" />
          </div>
          <div class="flex flex-col gap-1 max-w-md">
            <span class="text-base font-medium text-n-slate-12">
              {{ t('CUSTOMER_NOTIFICATIONS.EMPTY.TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.EMPTY.SUBTITLE') }}
            </span>
          </div>
        </div>
        <div v-else class="flex flex-col gap-3">
          <CardLayout
            v-for="record in records"
            :key="record.id"
            layout="row"
            selectable
            class="cursor-pointer hover:bg-n-alpha-1 transition-colors"
            @click="openDetail(record)"
          >
            <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
              <div class="flex items-center gap-2 w-full">
                <span
                  class="text-xs font-medium inline-flex items-center gap-1 h-6 px-2 py-0.5 rounded-md"
                  :class="CATEGORY_STYLE[record.category] || 'bg-n-alpha-2 text-n-slate-12'"
                >
                  <Icon :icon="CATEGORY_ICON[record.category] || 'i-lucide-bell'" class="size-3" />
                  {{ categoryLabel(record.category) }}
                </span>
                <span class="text-base font-medium text-n-slate-12 line-clamp-1 flex-1 min-w-0">
                  {{ record.title }}
                </span>
                <span
                  v-if="(record.read_count || 0) < (record.recipient_count || 1)"
                  class="size-2 rounded-full bg-n-brand flex-shrink-0"
                  :title="t('CUSTOMER_NOTIFICATIONS.UNREAD_DOT')"
                />
              </div>
              <div class="text-sm text-n-slate-11 line-clamp-1 w-full">
                {{ record.body }}
              </div>
              <div class="flex items-center w-full h-6 gap-3 overflow-hidden text-xs text-n-slate-11">
                <!-- Recipients: either single contact pill or count + avatars -->
                <span v-if="record.recipient_count > 1" class="inline-flex items-center gap-1.5 min-w-0">
                  <div class="flex -space-x-1.5">
                    <Avatar
                      v-for="(c, idx) in (record.sample_recipients || []).slice(0, 3)"
                      :key="c.id"
                      :name="c.name || 'Contact'"
                      :src="c.thumbnail || ''"
                      :size="18"
                      rounded-full
                      :class="`ring-2 ring-n-solid-2 z-${30 - idx * 10}`"
                    />
                  </div>
                  <span class="font-medium text-n-slate-12">
                    {{ t('CUSTOMER_NOTIFICATIONS.RECIPIENTS_COUNT', { n: record.recipient_count }) }}
                  </span>
                </span>
                <button
                  v-else-if="record.sample_recipients?.[0]"
                  type="button"
                  class="inline-flex items-center gap-1.5 hover:text-n-brand transition-colors min-w-0 truncate"
                  @click="openContactById(record.sample_recipients[0].id, $event)"
                >
                  <Avatar
                    :name="record.sample_recipients[0].name || 'Contact'"
                    :src="record.sample_recipients[0].thumbnail || ''"
                    :size="18"
                    rounded-full
                  />
                  <span class="font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors">
                    {{ record.sample_recipients[0].name || `#${record.sample_recipients[0].id}` }}
                  </span>
                </button>
                <span v-else class="inline-flex items-center gap-1 text-n-slate-11">
                  <Icon icon="i-lucide-user-round" class="size-3" />
                  <code class="text-xs font-mono">anon · {{ (record.batch_id || '').slice(0, 6) }}</code>
                </span>
                <span class="text-n-slate-9">·</span>
                <span class="inline-flex items-center gap-1 flex-shrink-0">
                  <Icon icon="i-lucide-clock-3" class="size-3" />
                  {{ formatRelativeOrDate(record.created_at) }}
                </span>
                <span class="text-n-slate-9">·</span>
                <span class="inline-flex items-center gap-1 flex-shrink-0">
                  <Icon icon="i-lucide-check-check" class="size-3" />
                  {{ deliveredTotal(record) }}
                </span>
                <span v-if="record.recipient_count > 1" class="text-n-slate-9">·</span>
                <span v-if="record.recipient_count > 1" class="inline-flex items-center gap-1 flex-shrink-0">
                  <Icon icon="i-lucide-eye" class="size-3" />
                  {{ record.read_count || 0 }} / {{ record.recipient_count }}
                </span>
              </div>
            </div>
            <Icon icon="i-lucide-chevron-right" class="text-n-slate-10 size-5 flex-shrink-0" />
          </CardLayout>

          <div v-if="meta.hasMore" class="flex justify-center pt-2">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="uiFlags.appending ? t('CUSTOMER_NOTIFICATIONS.LOADING') : t('CUSTOMER_NOTIFICATIONS.LOAD_MORE')"
              :disabled="uiFlags.appending"
              @click="loadMore"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- Detail Dialog -->
    <Dialog
      ref="detailDialogRef"
      width="2xl"
      position="top"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDetailClose"
    >
      <template #description>
        <!-- We render our own header inside content; suppress description -->
      </template>
      <div v-if="uiFlags.fetchingDetail" class="flex items-center justify-center py-10">
        <Spinner />
      </div>
      <div v-else-if="detail" class="flex flex-col gap-5">
        <!-- Header -->
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-2 min-w-0">
            <span
              class="text-xs font-medium inline-flex items-center gap-1 h-6 px-2 py-0.5 rounded-md w-fit"
              :class="CATEGORY_STYLE[detail.category] || 'bg-n-alpha-2 text-n-slate-12'"
            >
              <Icon :icon="CATEGORY_ICON[detail.category] || 'i-lucide-bell'" class="size-3" />
              {{ categoryLabel(detail.category) }}
            </span>
            <h3 class="text-lg font-semibold text-n-slate-12 leading-tight">
              {{ detail.title }}
            </h3>
          </div>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            @click="detailDialogRef?.close()"
          />
        </div>

        <!-- Body -->
        <div class="p-4 rounded-xl bg-n-alpha-black2 text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap">
          {{ detail.body }}
        </div>

        <!-- Aggregate stats -->
        <div class="grid grid-cols-4 gap-3">
          <div class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-teal-3">
            <span class="text-[11px] font-medium text-n-teal-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.DELIVERED') }}
            </span>
            <span class="text-lg font-semibold text-n-teal-11">{{ detail.delivery?.delivered || 0 }}</span>
          </div>
          <div class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-amber-3">
            <span class="text-[11px] font-medium text-n-amber-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.NO_PERMISSION') }}
            </span>
            <span class="text-lg font-semibold text-n-amber-11">{{ detail.delivery?.no_permission || 0 }}</span>
          </div>
          <div class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-alpha-2">
            <span class="text-[11px] font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.NO_DEVICE_REGISTERED') }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12">{{ detail.delivery?.no_device_registered || 0 }}</span>
          </div>
          <div class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-ruby-3">
            <span class="text-[11px] font-medium text-n-ruby-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.FAILED') }}
            </span>
            <span class="text-lg font-semibold text-n-ruby-11">{{ detail.delivery?.failed || 0 }}</span>
          </div>
        </div>

        <!-- Meta grid -->
        <div class="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{ t('CUSTOMER_NOTIFICATIONS.DETAIL.RECIPIENTS_COUNT') }}</span>
            <span class="text-n-slate-12 font-medium">
              {{ detail.recipient_count }}
              <span class="text-n-slate-11">·</span>
              <span class="text-n-slate-11 text-xs">
                {{ t('CUSTOMER_NOTIFICATIONS.DETAIL.READ_RATIO', { read: detail.read_summary?.read || 0, total: detail.recipient_count }) }}
              </span>
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{ t('CUSTOMER_NOTIFICATIONS.DETAIL.SENT_BY') }}</span>
            <button
              v-if="detail.sender?.type === 'user'"
              type="button"
              class="inline-flex items-center gap-2 hover:text-n-brand transition-colors w-fit"
              @click="openAgent(detail.sender); detailDialogRef?.close()"
            >
              <Avatar
                :name="detail.sender.name || 'Agent'"
                :src="detail.sender.thumbnail || ''"
                :size="20"
                rounded-full
              />
              <span class="text-sm font-medium text-n-slate-12 hover:text-n-brand transition-colors">
                {{ detail.sender.name || detail.sender.email || `#${detail.sender.id}` }}
              </span>
              <Icon icon="i-lucide-arrow-up-right" class="size-3 text-n-slate-11" />
            </button>
            <span
              v-else-if="detail.sender?.type === 'service'"
              class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-alpha-2 w-fit text-xs"
            >
              <Icon icon="i-lucide-cog" class="size-3 text-n-slate-11" />
              <code class="text-n-slate-12 font-mono">{{ detail.sender.label }}</code>
            </span>
            <code v-else class="text-n-slate-12 font-mono text-xs">{{ detail.sent_by_service }}</code>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{ t('CUSTOMER_NOTIFICATIONS.DETAIL.CREATED_AT') }}</span>
            <span class="text-n-slate-12">{{ formatDateTime(detail.created_at) }}</span>
          </div>
          <div v-if="detail.tag" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{ t('CUSTOMER_NOTIFICATIONS.DETAIL.TAG') }}</span>
            <code class="text-n-slate-12 font-mono text-xs">{{ detail.tag }}</code>
          </div>
          <div v-if="detail.deep_link" class="flex flex-col gap-1 col-span-2">
            <span class="text-xs font-medium text-n-slate-11">{{ t('CUSTOMER_NOTIFICATIONS.DETAIL.URL') }}</span>
            <a
              v-if="isAbsoluteUrl(detail.deep_link)"
              :href="detail.deep_link"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 text-n-blue-11 hover:text-n-blue-10 transition-colors w-fit"
            >
              <span class="font-mono text-xs break-all">{{ detail.deep_link }}</span>
              <Icon icon="i-lucide-external-link" class="size-3 flex-shrink-0" />
            </a>
            <code v-else class="text-n-slate-12 font-mono text-xs break-all">{{ detail.deep_link }}</code>
          </div>
        </div>

        <!-- Attribution -->
        <div v-if="detail.attribution && Object.keys(detail.attribution).length" class="flex flex-col gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOMER_NOTIFICATIONS.DETAIL.ATTRIBUTION') }}
          </h4>
          <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm p-3 rounded-lg bg-n-alpha-black2">
            <div v-for="(value, key) in detail.attribution" :key="key" class="flex flex-col gap-0.5">
              <span class="text-xs text-n-slate-11">{{ key }}</span>
              <code class="text-n-slate-12 font-mono text-xs break-all">{{ typeof value === 'object' ? JSON.stringify(value) : value }}</code>
            </div>
          </div>
        </div>

        <!-- Per-recipient breakdown -->
        <div class="flex flex-col gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOMER_NOTIFICATIONS.DETAIL.RECIPIENTS') }}
          </h4>
          <div class="flex flex-col gap-1.5 max-h-80 overflow-y-auto">
            <div
              v-for="r in (detail.recipients || [])"
              :key="r.notification_id"
              class="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-n-alpha-black2"
            >
              <button
                v-if="r.contact"
                type="button"
                class="flex items-center gap-2.5 min-w-0 flex-1 text-left hover:text-n-brand transition-colors"
                @click="openContactById(r.contact.id); detailDialogRef?.close()"
              >
                <Avatar
                  :name="r.contact.name || 'Contact'"
                  :src="r.contact.thumbnail || ''"
                  :size="28"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ r.contact.name || `#${r.contact.id}` }}
                  </span>
                  <span v-if="r.contact.email" class="text-xs text-n-slate-11 truncate">
                    {{ r.contact.email }}
                  </span>
                </div>
              </button>
              <div v-else class="flex items-center gap-2 flex-1 min-w-0 text-xs text-n-slate-11">
                <Icon icon="i-lucide-user-round" class="size-4" />
                <code class="font-mono truncate">
                  {{ r.vivacrm_user_id ? `user · ${r.vivacrm_user_id.slice(0, 8)}` : `anon · ${(r.anon_device_id || '').slice(0, 8)}` }}
                </code>
              </div>
              <div class="flex items-center gap-3 flex-shrink-0">
                <span
                  class="text-xs font-medium inline-flex items-center gap-1 px-2 py-1 rounded-md"
                  :class="recipientPillClass(r)"
                >
                  <Icon :icon="recipientPillIcon(r)" class="size-3.5" />
                  {{ recipientPillLabel(r) }}
                </span>
                <span class="text-xs text-n-slate-11 font-mono whitespace-nowrap">
                  {{ r.delivery?.delivered || 0 }} / {{ r.delivery?.total_devices || 0 }}
                </span>
                <span v-if="r.read_at" :title="t('CUSTOMER_NOTIFICATIONS.DETAIL.READ_AT') + ': ' + formatDateTime(r.read_at)">
                  <Icon icon="i-lucide-eye" class="size-3.5 text-n-teal-11" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Create dialog -->
    <CreateCustomerNotificationDialog ref="createDialogRef" @sent="onSent" />

    <!-- Send result dialog (per-recipient breakdown) -->
    <Dialog
      ref="resultDialogRef"
      width="2xl"
      position="top"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="closeResult"
    >
      <div v-if="sendResult" class="flex flex-col gap-5">
        <!-- Header -->
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-1">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('CUSTOMER_NOTIFICATIONS.RESULT.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.RESULT.SUBTITLE', { n: sendResult.summary?.total || 0 }) }}
            </p>
          </div>
          <Button variant="ghost" color="slate" size="sm" icon="i-lucide-x" @click="closeResult" />
        </div>

        <!-- Aggregate stats -->
        <div class="grid grid-cols-4 gap-3">
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-teal-3">
            <span class="text-xs font-medium text-n-teal-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.DELIVERED') }}
            </span>
            <span class="text-xl font-semibold text-n-teal-11">{{ sendResult.summary?.delivered || 0 }}</span>
          </div>
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-amber-3">
            <span class="text-xs font-medium text-n-amber-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.NO_PERMISSION') }}
            </span>
            <span class="text-xl font-semibold text-n-amber-11">{{ sendResult.summary?.no_permission || 0 }}</span>
          </div>
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-alpha-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.NO_DEVICE_REGISTERED') }}
            </span>
            <span class="text-xl font-semibold text-n-slate-12">{{ sendResult.summary?.no_device_registered || 0 }}</span>
          </div>
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-ruby-3">
            <span class="text-xs font-medium text-n-ruby-11">
              {{ t('CUSTOMER_NOTIFICATIONS.DELIVERY_STATUS.FAILED') }}
            </span>
            <span class="text-xl font-semibold text-n-ruby-11">{{ sendResult.summary?.failed || 0 }}</span>
          </div>
        </div>

        <!-- Per-recipient -->
        <div class="flex flex-col gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOMER_NOTIFICATIONS.RESULT.PER_RECIPIENT') }}
          </h4>
          <div class="flex flex-col gap-1.5">
            <div
              v-for="result in sendResult.results"
              :key="result.contact_id"
              class="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-n-alpha-black2 hover:bg-n-alpha-1 transition-colors"
              :class="result.notification_id ? 'cursor-pointer' : ''"
              @click="openNotificationFromResult(result.notification_id)"
            >
              <div class="flex items-center gap-2.5 min-w-0 flex-1">
                <Avatar
                  :name="result.contact?.name || 'Contact'"
                  :src="result.contact?.thumbnail || ''"
                  :size="28"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ result.contact?.name || `#${result.contact_id}` }}
                  </span>
                  <span v-if="result.contact?.email" class="text-xs text-n-slate-11 truncate">
                    {{ result.contact.email }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-3 flex-shrink-0">
                <!-- Status pill -->
                <span
                  class="text-xs font-medium inline-flex items-center gap-1 px-2 py-1 rounded-md"
                  :class="TONE_CLASS[recipientResultStatus(result).tone]"
                >
                  <Icon
                    :icon="recipientResultStatus(result).tone === 'delivered' ? 'i-lucide-check-check' :
                           recipientResultStatus(result).tone === 'warn' ? 'i-lucide-bell-off' :
                           recipientResultStatus(result).tone === 'failed' ? 'i-lucide-circle-x' : 'i-lucide-info'"
                    class="size-3.5"
                  />
                  {{ t(`CUSTOMER_NOTIFICATIONS.RESULT.STATUS.${recipientResultStatus(result).key.toUpperCase()}`) }}
                </span>
                <!-- Device count -->
                <span v-if="result.delivery" class="text-xs text-n-slate-11 font-mono whitespace-nowrap">
                  {{ result.delivery.delivered || 0 }} / {{ result.delivery.total_devices || 0 }}
                </span>
                <Icon v-if="result.notification_id" icon="i-lucide-chevron-right" class="size-4 text-n-slate-10" />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end pt-2 border-t border-n-weak">
          <Button color="slate" variant="faded" size="sm" :label="t('CUSTOMER_NOTIFICATIONS.RESULT.CLOSE')" @click="closeResult" />
        </div>
      </div>
    </Dialog>
  </section>
</template>
